import type { LotCostSource } from "../../api/types";
import { amountOf, cost, money, NO_AMOUNT } from "../../i18n/intl";
import { t as translate } from "../../i18n/t";
import { estimateTargetPriceEur } from "../../utils/priceFloor";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WO-B part 7 (G-139, OD-198) — a cost the kiosk does not know is not €0
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The ONE place the tablet decides how a cost the server may not know reads.
 * Every kiosk screen that shows a purchase cost, a bouquet's cost or a
 * bouquet's floor goes through here: the till, the bouquet builder, the
 * "Bouquets" tab of See stock and the item card. A second copy of any of
 * these rules on a screen is how one of them ends up printing €0.00.
 *
 * The words are OD-198's: a cost nobody knows reads "Cost unknown", a figure
 * that cannot be worked out reads "—" (`NO_AMOUNT`), a floor that cannot be
 * worked out reads "Floor: cannot check". Labels and pills only — no
 * sentence explains it on screen (owner rule 2026-10-13).
 *
 * The office has `utils/lotCostSource.ts` for the same lot marker. It is not
 * reused here because it returns English sentences for a page outside the
 * five-language layer and tells the two kinds of estimate apart; the kiosk
 * says "Estimated" for both, through the dictionaries.
 *
 * Text is read at CALL time (the module `t`), so callers are components that
 * already subscribe with `useT()` — the same contract as `money` and `cost`.
 */


/** One cost on screen: the amount when known, and a pill beside it, or none. */
export interface CostFigure {
  /** Formatted; null when the cost is not known (the pill then says so). */
  amount: string | null;
  /** "Cost unknown" or "Estimated"; null for a plain known cost. */
  pill: string | null;
}

/**
 * A unit cost: the builder's next-stem cost on a card or a line, up to four
 * decimals (`cost`) so a fraction of a cent never reads as €0.00. `null` is
 * "Cost unknown"; `undefined` — the feed has said nothing about the item yet,
 * or it is not in stock here — has neither an amount nor a pill, and the
 * screen shows "—" (`NO_AMOUNT`).
 */
export function unitCostFigure(value: string | number | null | undefined): CostFigure {
  if (value === undefined) return { amount: null, pill: null };
  const n = amountOf(value);
  return n == null
    ? { amount: null, pill: translate("kioskCost.costUnknown") }
    : { amount: cost(n), pill: null };
}

/**
 * A lot's unit cost on the item card. Null — show nothing at all — when the
 * server sent no cost: a florist never sees a cost, nor that one is unknown
 * or estimated (G-73; the server leaves both keys out for them).
 */
export function lotCostFigure(lot: {
  purchaseUnitCostEur?: string | null;
  costSource?: LotCostSource;
}): CostFigure | null {
  if (lot.purchaseUnitCostEur === undefined) return null;
  const n = amountOf(lot.purchaseUnitCostEur);
  // The database holds "no cost exactly when UNKNOWN"; either side says it.
  if (n == null || lot.costSource === "UNKNOWN") {
    return { amount: null, pill: translate("kioskCost.costUnknown") };
  }
  const estimated =
    lot.costSource === "ESTIMATED_LAST_LOT" || lot.costSource === "ESTIMATED_CATALOG";
  return { amount: cost(n), pill: estimated ? translate("kioskCost.estimated") : null };
}

/** A finished bouquet's cost, as the save result and the stock card show it. */
export interface BouquetCostFigure {
  /** Some component has no cost: the amount is the known part only. */
  partial: boolean;
  /** The (known) cost, or "—" when the build carries none. */
  amount: string;
  /** "2 items: cost unknown" when partial. */
  pill: string | null;
}

export function bouquetCostFigure(build: {
  totalCogsEur: string | null;
  unknownCostComponentCount?: number | null;
}): BouquetCostFigure {
  const unknown = build.unknownCostComponentCount ?? 0;
  const known = amountOf(build.totalCogsEur);
  return {
    partial: unknown > 0,
    amount: known == null ? NO_AMOUNT : money(known),
    pill: unknownCountPill(unknown),
  };
}

/**
 * A shelf bouquet's floor at the till. "cannotCheck" whenever part of its
 * cost is unknown (OD-198) — whatever else the row says — so the till never
 * warns "below the floor" for it; "none" when the build carries no floor at
 * all (no cost recorded), which the till leaves silent as before.
 *
 * Reads `unknownCostComponentCount`, the same field `bouquetCostFigure`
 * reads, so the till's pill and the stock card agree about one bouquet; the
 * server's `costIncomplete` (derived from it) is honoured too, never ignored.
 */
export type BouquetFloor =
  | { kind: "floor"; eur: number }
  | { kind: "cannotCheck" }
  | { kind: "none" };

export function bouquetFloor(bouquet: {
  floorPriceEur: string | null;
  unknownCostComponentCount?: number | null;
  costIncomplete?: boolean;
}): BouquetFloor {
  // Either field saying "incomplete" is enough — the safe direction if one is
  // ever missing from a row (an API older or newer than this bundle).
  if ((bouquet.unknownCostComponentCount ?? 0) > 0 || bouquet.costIncomplete === true) {
    return { kind: "cannotCheck" };
  }
  const eur = amountOf(bouquet.floorPriceEur);
  return eur == null ? { kind: "none" } : { kind: "floor", eur };
}

/** The floor pill a ticket line shows; null when there is nothing to say. */
export function bouquetFloorPill(floor: BouquetFloor): string | null {
  return floor.kind === "cannotCheck" ? translate("kioskCost.floorCannotCheck") : null;
}

/**
 * The floor a price is under, in euro — only a known floor can be breached;
 * null otherwise ("cannot check", no floor, at or above it, or no price yet).
 */
export function floorBreachedEur(price: number, floor: BouquetFloor): number | null {
  return floor.kind === "floor" && Number.isFinite(price) && price > 0 && price < floor.eur
    ? floor.eur
    : null;
}

/** A bouquet about to be sold: its name, the price it will carry, its floor. */
export interface BouquetFloorLine {
  label: string;
  price: number;
  floor: BouquetFloor;
}

/**
 * G-45 part 3: the till's warning BEFORE a sale — one sentence per bouquet
 * priced under its floor, naming the floor; never blocking. A bouquet whose
 * cost is incomplete has no floor to warn against (OD-198). The till and
 * "Customer collected" (G-177, KI-132) both warn through here.
 */
export function belowFloorWarnings(lines: readonly BouquetFloorLine[]): string[] {
  return lines.flatMap((line) => {
    const floorEur = floorBreachedEur(line.price, line.floor);
    if (floorEur == null) return [];
    return [
      translate("sale.belowFloorWarning", {
        label: line.label || translate("sale.bouquet"),
        price: money(line.price),
        floor: money(floorEur),
      }),
    ];
  });
}

/**
 * The bouquet builder's live figures, before saving (G-45's estimate).
 *
 * G-175 (KI-155): each line's stem cost is the SERVER's, worked out the way
 * saving takes the stems — FIFO across lots, each slice at its own lot's cost
 * (`POST /shop-mode/bouquet/estimate`). A line is `undefined` until the
 * server has answered for it; `enoughStock: false` when the shop has too few
 * (the save would refuse); otherwise its known part and the units drawn from
 * lots of unknown cost. The browser adds the lines' known parts up and
 * nothing else: no FIFO walk, no unit cost × quantity here.
 *
 * A price is worked out ONLY when every line is wholly costed: a price from
 * the known part would understate it (OD-198). With a unit of unknown cost on
 * any line it is "costUnknown" (the panel shows the known cost and the count
 * of lines); with a line not costed yet, or short of stock, "notCosted" (just
 * "—": nothing is unknown, the figure is missing); "noRule" when the Flower
 * category's VAT rate or margin is missing or out of range. The known cost
 * adds only the known parts — null when there is none (never a €0 that reads
 * as a cost). Paper is costed by the server at save.
 */
export interface BuilderCostEstimate {
  knownCostEur: number | null;
  unknownCostLines: number;
  price:
    | { kind: "price"; eur: number }
    | { kind: "costUnknown" }
    | { kind: "notCosted" }
    | { kind: "noRule" };
}

/** One draft line's stem cost, as the server's estimate answers it. */
export interface LineStemCost {
  /** The known part (net); null when no unit of the line has a known cost. */
  knownCostEur: string | number | null;
  /** Units drawn from lots of unknown cost. */
  unknownCostQty: string | number;
  enoughStock: boolean;
}

export function builderCostEstimate(
  lines: ReadonlyArray<LineStemCost | undefined>,
  rule: { vatRatePercent: number; targetMarginPercent: number } | null,
): BuilderCostEstimate {
  let known = 0;
  let knownLines = 0;
  let unknownCostLines = 0;
  let uncostedLines = 0;
  for (const line of lines) {
    if (line === undefined || !line.enoughStock) {
      uncostedLines += 1;
      continue;
    }
    const knownPart = amountOf(line.knownCostEur);
    const unknownQty = amountOf(line.unknownCostQty) ?? 0;
    if (knownPart != null) {
      known += knownPart;
      knownLines += 1;
    }
    if (unknownQty > 0) unknownCostLines += 1;
    else if (knownPart == null) uncostedLines += 1;
  }
  const knownCostEur = knownLines > 0 ? known : null;
  if (unknownCostLines > 0) {
    return { knownCostEur, unknownCostLines, price: { kind: "costUnknown" } };
  }
  if (uncostedLines > 0) {
    return { knownCostEur, unknownCostLines, price: { kind: "notCosted" } };
  }
  const eur = rule
    ? estimateTargetPriceEur(known, rule.vatRatePercent, rule.targetMarginPercent)
    : null;
  return {
    knownCostEur,
    unknownCostLines,
    price: eur == null ? { kind: "noRule" } : { kind: "price", eur },
  };
}

/**
 * One draft line's cost as the builder line shows it (G-175, KI-155): the
 * server's known part when the whole line is costed; "Cost unknown" when any
 * of its units come from a lot of unknown cost; "—" while the server has not
 * answered, or the shop is short of it.
 */
export function stemLineCostText(line: LineStemCost | undefined): string {
  if (line === undefined || !line.enoughStock) return NO_AMOUNT;
  if ((amountOf(line.unknownCostQty) ?? 0) > 0) return translate("kioskCost.costUnknown");
  const known = amountOf(line.knownCostEur);
  return known == null ? NO_AMOUNT : money(known);
}

/**
 * "2 items: cost unknown" — the components (items) with no cost, for a saved
 * build and for the builder's live figures alike; null when there are none.
 */
export function unknownCountPill(count: number): string | null {
  return count > 0 ? translate("kioskCost.itemsCostUnknown", { count }) : null;
}
